import React, { useState, useCallback, useEffect, forwardRef, useRef } from "react"

import { PHONE_ERROR_TEXT } from "components/PhoneInput/common/constants"
import { parsePhoneNumber } from "utils/phone-number"
import { Input } from "@components-branded/FormControls/Input/Input"
import { InputProps } from "@components-branded/FormControls/Input/types"

interface Props extends InputProps {
    enableValidation?: boolean
    required?: boolean
    defaultValue?: string
    showErrors?: boolean
    className?: string
    errorCallback?: (e: boolean) => void
    autoFocus?: boolean
    onChangeValue?: (value: string) => void
}

export const PhoneInput = forwardRef<HTMLInputElement, Props>(
    (
        {
            enableValidation,
            showErrors,
            errorCallback,
            required,
            defaultValue,
            className,
            name,
            label,
            autoFocus,
            onChangeValue,
            placeholder,
        }: Props,
        ref
    ) => {
        const defaultValueLocal = typeof defaultValue === "undefined" ? "" : String(defaultValue)
        const [value, setValue] = useState("")
        const [error, setError] = useState("")
        const isInitialRender = useRef(true)

        const handleChange = (value: string) => {
            const valueWithStream = value.trim()
            setValue(valueWithStream)
            phoneValidate(valueWithStream)
        }

        useEffect(() => {
            errorCallback && errorCallback(!!error)
        }, [error])

        useEffect(() => {
            phoneValidate(value)
        }, [value])

        useEffect(() => {
            showErrors && phoneValidate(defaultValueLocal || value)
        }, [showErrors])

        useEffect(() => {
            if (isInitialRender.current) {
                isInitialRender.current = false
                return
            }
            onChangeValue?.(value)
        }, [value])

        const phoneValidate = (value: string | undefined) => {
            if (!value && required) {
                setError(PHONE_ERROR_TEXT.empty)
            }
            if (value) {
                const phoneNumber = parsePhoneNumber(value)
                if (!phoneNumber.isValid) {
                    setError(PHONE_ERROR_TEXT.default)
                } else {
                    setValue(phoneNumber.internationalFormat || "")
                    setError("")
                }
            }
        }

        const handleBlur = useCallback(() => {
            enableValidation && phoneValidate(value)
        }, [value, enableValidation])

        useEffect(() => {
            setValue(defaultValueLocal)
            phoneValidate(defaultValueLocal)
        }, [defaultValueLocal])

        return (
            <Input
                ref={ref}
                type="tel"
                value={value}
                error={showErrors ? error : undefined}
                onChange={handleChange}
                onBlur={handleBlur}
                className={className}
                name={name}
                label={label}
                autoFocus={autoFocus}
                placeholder={placeholder}
            />
        )
    }
)

PhoneInput.defaultProps = {
    enableValidation: false,
    showErrors: false,
}

PhoneInput.displayName = "PhoneInput"
